import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { accountService } from '../../../services/accountService';
import { categoryService } from '../../../services/categoryService';

const EditBillModal = ({ bill, onClose, onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [formData, setFormData] = useState({
    transactionName: bill?.transactionName || '',
    description: bill?.description || '',
    amount: bill?.amount || '',
    accountId: bill?.accountId || '',
    categoryId: bill?.categoryId || '',
    subcategoryId: bill?.subcategoryId || '',
    frequency: bill?.frequency || 'monthly',
    startDate: bill?.startDate || new Date()?.toISOString()?.split('T')?.[0],
    nextExecutionDate: bill?.nextExecutionDate || new Date()?.toISOString()?.split('T')?.[0],
    status: bill?.status || 'active',
    type: bill?.type || 'expense',
    monthlyOverflowRule: bill?.monthlyOverflowRule || 'last_day',
    semiMonthlyRule: bill?.semiMonthlyRule || '1st_and_15th',
    color: bill?.color || '#a855f7'
  });

  const PRESET_COLORS = [
    { name: 'Amethyst', value: '#a855f7' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Green', value: '#22c55e' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Pink', value: '#ec4899' },
    { name: 'Yellow', value: '#eab308' },
    { name: 'Teal', value: '#14b8a6' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Filter categories based on selected type
    if (formData?.type) {
      const filtered = allCategories?.filter(cat => 
        cat?.type === formData?.type && !cat?.parentId
      );
      setFilteredCategories(filtered);
    }
  }, [formData?.type, allCategories]);

  useEffect(() => {
    // Load subcategories when category is selected
    if (formData?.categoryId) {
      loadSubcategories(formData?.categoryId);
    } else {
      setSubcategories([]);
    }
  }, [formData?.categoryId]);

  const loadData = async () => {
    try {
      const [accountsData, categoriesData] = await Promise.all([
        accountService?.getAll(),
        categoryService?.getAll()
      ]);
      
      if (accountsData && Array.isArray(accountsData)) {
        setAccounts(accountsData);
      }
      
      if (categoriesData && Array.isArray(categoriesData)) {
        setAllCategories(categoriesData);
        // Initial filter based on bill type
        const filtered = categoriesData?.filter(cat => 
          cat?.type === (bill?.type || 'expense') && !cat?.parentId
        );
        setFilteredCategories(filtered);
      }

      // Load subcategories if category is already selected
      if (bill?.categoryId) {
        loadSubcategories(bill?.categoryId);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load accounts and categories');
    }
  };

  const loadSubcategories = async (parentId) => {
    try {
      const subs = await categoryService?.getSubcategories(parentId);
      setSubcategories(subs || []);
    } catch (err) {
      console.error('Error loading subcategories:', err);
      setSubcategories([]);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.transactionName || !formData?.transactionName?.trim()) {
      setError('Name is required');
      return;
    }
    
    if (!formData?.amount || parseFloat(formData?.amount) <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    if (!formData?.categoryId) {
      setError('Please select a category');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const billData = {
        transactionName: formData?.transactionName?.trim(),
        description: formData?.description?.trim(),
        amount: parseFloat(formData?.amount),
        accountId: formData?.accountId || null,
        categoryId: formData?.categoryId,
        subcategoryId: formData?.subcategoryId || null,
        frequency: formData?.frequency,
        startDate: formData?.startDate,
        nextExecutionDate: formData?.nextExecutionDate,
        status: formData?.status,
        type: formData?.type,
        monthlyOverflowRule: formData?.monthlyOverflowRule,
        semiMonthlyRule: formData?.semiMonthlyRule,
        color: formData?.color
      };

      await onSubmit(billData);
    } catch (err) {
      console.error('Error updating recurring item:', err);
      setError(err?.message || 'Failed to update recurring item');
      setLoading(false);
    }
  };

  const selectedAccount = accounts?.find(acc => acc?.id === formData?.accountId);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-20 bg-background/80 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-card rounded-lg shadow-elevation-4 border border-border w-full max-w-[600px] max-h-[90vh] overflow-y-auto" onClick={(e) => e?.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-24 border-b border-border">
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Edit Recurring Item
          </h2>
          <button
            onClick={onClose}
            className="p-8 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-24 space-y-20">
          {error && (
            <div className="p-12 bg-error/10 border border-error/20 rounded-lg flex items-start gap-12">
              <Icon name="AlertCircle" size={18} color="var(--color-error)" />
              <p className="text-sm text-error flex-1">{error}</p>
            </div>
          )}

          {/* Type Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Type *
            </label>
            <div className="grid grid-cols-2 gap-12">
              <button
                type="button"
                onClick={() => handleChange('type', 'expense')}
                className={`px-16 py-12 rounded-lg border-2 text-sm font-medium transition-all ${
                  formData?.type === 'expense' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-foreground hover:border-primary/50'
                }`}
              >
                <Icon name="TrendingDown" size={18} color={formData?.type === 'expense' ? 'var(--color-primary)' : 'var(--color-foreground)'} className="inline mr-8" />
                Recurring Expense
              </button>
              <button
                type="button"
                onClick={() => handleChange('type', 'income')}
                className={`px-16 py-12 rounded-lg border-2 text-sm font-medium transition-all ${
                  formData?.type === 'income' ?'border-success bg-success/10 text-success' :'border-border bg-background text-foreground hover:border-success/50'
                }`}
              >
                <Icon name="TrendingUp" size={18} color={formData?.type === 'income' ? 'var(--color-success)' : 'var(--color-foreground)'} className="inline mr-8" />
                Recurring Income
              </button>
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Name *
            </label>
            <input
              type="text"
              value={formData?.transactionName}
              onChange={(e) => handleChange('transactionName', e?.target?.value)}
              placeholder={formData?.type === 'expense' ? 'e.g., Netflix Subscription' : 'e.g., Freelance Payment'}
              className="w-full px-16 py-12 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              required
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Amount *
            </label>
            <div className="relative">
              <span className="absolute left-16 top-1/2 transform -translate-y-1/2 text-sm text-muted-foreground">
                $
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData?.amount}
                onChange={(e) => handleChange('amount', e?.target?.value)}
                placeholder="0.00"
                className="w-full pl-32 pr-16 py-12 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                required
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Category *
            </label>
            <select
              value={formData?.categoryId}
              onChange={(e) => handleChange('categoryId', e?.target?.value)}
              className="w-full px-16 py-12 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              required
            >
              <option value="">Select category</option>
              {filteredCategories?.map(category => (
                <option key={category?.id} value={category?.id}>
                  {category?.emoji ? `${category?.emoji} ` : ''}{category?.name}
                </option>
              ))}
            </select>
          </div>

          {/* Subcategory (Optional) */}
          {subcategories?.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Subcategory <span className="text-muted-foreground text-xs">(Optional)</span>
              </label>
              <select
                value={formData?.subcategoryId}
                onChange={(e) => handleChange('subcategoryId', e?.target?.value)}
                className="w-full px-16 py-12 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">None</option>
                {subcategories?.map(subcategory => (
                  <option key={subcategory?.id} value={subcategory?.id}>
                    {subcategory?.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Account */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Account <span className="text-muted-foreground text-xs">(Optional)</span>
            </label>
            <select
              value={formData?.accountId}
              onChange={(e) => handleChange('accountId', e?.target?.value)}
              className="w-full px-16 py-12 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">No account linked</option>
              {accounts?.map(account => (
                <option key={account?.id} value={account?.id}>
                  {account?.name} ({account?.type})
                </option>
              ))}
            </select>
            {selectedAccount && (
              <p className="mt-8 text-xs text-muted-foreground">
                This recurring item will affect: <span className="font-medium text-foreground">{selectedAccount?.name}</span>
              </p>
            )}
            {!formData?.accountId && (
              <p className="mt-8 text-xs text-muted-foreground">
                Without an account, this item will appear for tracking purposes only
              </p>
            )}
          </div>

          {/* Frequency */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Frequency *
            </label>
            <select
              value={formData?.frequency}
              onChange={(e) => handleChange('frequency', e?.target?.value)}
              className="w-full px-16 py-12 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              required
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Biweekly</option>
              <option value="semi-monthly">Semi-monthly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          {/* Semi-Monthly Rule (only show when semi-monthly is selected) */}
          {formData?.frequency === 'semi-monthly' && (
            <div className="p-16 bg-muted/50 border border-border rounded-lg space-y-12">
              <label className="block text-sm font-medium text-foreground">
                Semi-Monthly Schedule *
              </label>
              <div className="space-y-8">
                <label className="flex items-start gap-12 cursor-pointer group">
                  <input
                    type="radio"
                    name="semiMonthlyRule"
                    value="1st_and_15th"
                    checked={formData?.semiMonthlyRule === '1st_and_15th'}
                    onChange={(e) => handleChange('semiMonthlyRule', e?.target?.value)}
                    className="mt-4 w-16 h-16 text-primary focus:ring-2 focus:ring-primary/50"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                      1st and 15th of the month
                    </div>
                    <div className="text-xs text-muted-foreground mt-4">
                      Transactions occur on the 1st and 15th of each month
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-12 cursor-pointer group">
                  <input
                    type="radio"
                    name="semiMonthlyRule"
                    value="15th_and_last"
                    checked={formData?.semiMonthlyRule === '15th_and_last'}
                    onChange={(e) => handleChange('semiMonthlyRule', e?.target?.value)}
                    className="mt-4 w-16 h-16 text-primary focus:ring-2 focus:ring-primary/50"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                      15th and Last day of the month
                    </div>
                    <div className="text-xs text-muted-foreground mt-4">
                      Transactions occur on the 15th and last day of each month
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Monthly Overflow Rule (only show when monthly is selected) */}
          {formData?.frequency === 'monthly' && (
            <div className="p-16 bg-muted/50 border border-border rounded-lg space-y-12">
              <label className="block text-sm font-medium text-foreground">
                When date doesn't exist in month *
              </label>
              <p className="text-xs text-muted-foreground">
                If your transaction is scheduled for a day that doesn't exist in some months (e.g., 31st in February), choose how to handle it:
              </p>
              <div className="space-y-8">
                <label className="flex items-start gap-12 cursor-pointer group">
                  <input
                    type="radio"
                    name="monthlyOverflowRule"
                    value="last_day"
                    checked={formData?.monthlyOverflowRule === 'last_day'}
                    onChange={(e) => handleChange('monthlyOverflowRule', e?.target?.value)}
                    className="mt-4 w-16 h-16 text-primary focus:ring-2 focus:ring-primary/50"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                      Last day of the month
                    </div>
                    <div className="text-xs text-muted-foreground mt-4">
                      Use the last calendar day (e.g., Feb 28/29, Apr 30)
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-12 cursor-pointer group">
                  <input
                    type="radio"
                    name="monthlyOverflowRule"
                    value="last_business_day"
                    checked={formData?.monthlyOverflowRule === 'last_business_day'}
                    onChange={(e) => handleChange('monthlyOverflowRule', e?.target?.value)}
                    className="mt-4 w-16 h-16 text-primary focus:ring-2 focus:ring-primary/50"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                      Last business day of the month
                    </div>
                    <div className="text-xs text-muted-foreground mt-4">
                      Use the last weekday, excluding weekends (e.g., if Feb 28 is Saturday, use Feb 26)
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Next Payment Date */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Next Payment Date *
            </label>
            <input
              type="date"
              value={formData?.nextExecutionDate}
              onChange={(e) => handleChange('nextExecutionDate', e?.target?.value)}
              className="w-full px-16 py-12 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              required
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Status
            </label>
            <select
              value={formData?.status}
              onChange={(e) => handleChange('status', e?.target?.value)}
              className="w-full px-16 py-12 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Description <span className="text-muted-foreground text-xs">(Optional)</span>
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleChange('description', e?.target?.value)}
              placeholder="Add notes about this recurring item..."
              rows={3}
              className="w-full px-16 py-12 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
            />
          </div>

          {/* Color Picker */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Calendar Color
            </label>
            <p className="text-xs text-muted-foreground mb-12">
              Choose a color for this bill in the calendar view
            </p>
            <div className="grid grid-cols-4 gap-8">
              {PRESET_COLORS?.map(color => (
                <button
                  key={color?.value}
                  type="button"
                  onClick={() => handleChange('color', color?.value)}
                  className={`h-40 rounded-lg border-2 transition-all hover:scale-105 ${
                    formData?.color === color?.value ? 'border-foreground ring-2 ring-primary' : 'border-border'
                  }`}
                  style={{ backgroundColor: color?.value }}
                  title={color?.name}
                />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-12 pt-16">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-16 py-12 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-16 py-12 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBillModal;